import {
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dialog, Popover } from 'react-aria-components';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';
import { token } from '~/styled-system/tokens';
import { TicketIcon } from '~/ui/icons/ticket';
import { ChevronDownIcon } from '~/ui/icons/chevron-down';
import { TicketsSelector } from '~/ui/tickets-selector';
import {
  perBookingMax,
  PerBookingMaxHint,
} from '~/ui/tickets-selector/per-booking-max';
import { PickerRows } from './picker-rows';
import { TicketsDrawer } from './tickets-drawer';
import type { TicketVariantOption } from './types';

/** Imperative surface for opening the picker from outside (e.g. when the
 *  checkout's Continue validation wants to point the visitor at the field). */
type TicketsFieldHandle = {
  open: () => void;
};

type TicketsFieldProps = {
  /** Exposes `open()` to the parent. */
  ref?: Ref<TicketsFieldHandle>;
  /** Pre-formatted summary shown below the "Tickets" label, e.g. "2 adults".
   *  Unused while a lone variant renders inline — the picker itself is then the
   *  summary. */
  summary: string;
  /** Variants the visitor can pick quantities for. */
  variants: TicketVariantOption[];
  /** Current quantities keyed by variant/add-on id. */
  quantities: Record<string, number>;
  /** Step for seat variants (the experience's multiple-of rule). Add-ons
   *  always step by 1. Defaults to 1. */
  seatStep?: number;
  /** The experience's max tickets per booking. Caps the seat steppers and
   *  shows a hint once reached. Omitted or 0 means no max. */
  maxSeats?: number;
  /** Emits the full next quantities map (handles add-on resets atomically). */
  onQuantitiesChange: (next: Record<string, number>) => void;
  /** Renders the field non-interactive (dimmed, won't open). Used e.g. when a
   *  prerequisite selection is still missing. Defaults to false. */
  disabled?: boolean;
  /** Called when the picker popover opens. Never fires while a lone variant
   *  renders inline — there is no overlay to open. */
  onOpen?: () => void;
  /** Called when the mobile drawer commits via Save, with the saved
   *  quantities. The desktop popover commits live per change and never fires
   *  this, nor does the inline rendering of a lone variant. Fires after
   *  `onQuantitiesChange`, so read quantities from the argument — parent state
   *  is still one render behind. */
  onSave?: (next: Record<string, number>) => void;
  /** Validation tone — `'critical'` paints the field's border in the negative
   *  status color. Mirrors Leaf's form-field `tone`. Ignored while a lone
   *  variant renders inline, where there is no field border to paint and the
   *  error message carries the state on its own. */
  tone?: 'critical';
  /** Validation/error message rendered beneath the field. Styled critical by
   *  the field; the caller passes only the message content. */
  errorMessage?: ReactNode;
};

export type { TicketsFieldHandle, TicketsFieldProps };

/**
 * The launch field: a trigger summarising the selection, opening the picker in
 * an anchored popover (tablet+) or a bottom-sheet drawer (mobile).
 *
 * A single variant is not a choice, so hiding its stepper behind an overlay
 * only adds a tap: that case renders the flattened `TicketsSelector` inline
 * instead (US-10124). Quantities are still emitted through the same
 * `onQuantitiesChange` map, keyed by variant/add-on id, so the callers' basket
 * posts are identical either way.
 */
export function TicketsField({
  ref,
  summary,
  variants,
  quantities,
  seatStep = 1,
  maxSeats,
  onQuantitiesChange,
  onOpen,
  onSave,
  tone,
  errorMessage,
  disabled = false,
}: TicketsFieldProps) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [triggerWidth, setTriggerWidth] = useState<number>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inlineRef = useRef<HTMLDivElement>(null);
  const hintId = useId();
  const bookingMax = perBookingMax(maxSeats, variants, quantities);
  // An error already says what to fix; the hint would only repeat it.
  const hintMax =
    bookingMax.reached && !errorMessage ? bookingMax.max : undefined;

  // A lone variant renders inline. `disabled` keeps the trigger: the inline
  // picker has no non-interactive state to fall back on, and the field's job
  // there is to say "not yet" rather than to offer a stepper.
  const isInline = variants.length === 1 && !disabled;

  function openPicker() {
    if (isOpen || disabled) return;
    setTriggerWidth(triggerRef.current?.offsetWidth);
    // Decide the surface at open time — the trigger SSR-renders, but the
    // overlay only mounts client-side once open, so `window` is safe here.
    setIsMobile(
      !window.matchMedia(`(min-width: ${token('breakpoints.tablet')})`).matches
    );
    onOpen?.();
    setIsOpen(true);
  }

  function toggleOpen() {
    if (isOpen) setIsOpen(false);
    else openPicker();
  }

  // Inline has no overlay to open, but callers use `open()` to put the fix in
  // front of a visitor whose Continue just failed validation — scroll the
  // pickers into view instead of dropping the call.
  useImperativeHandle(ref, () => ({
    open: () =>
      isInline
        ? inlineRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        : openPicker(),
  }));

  // Keep the popover width in sync with the trigger while open (e.g. on
  // window resize or layout shifts).
  useEffect(() => {
    if (!isOpen) return;
    const el = triggerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setTriggerWidth(el.offsetWidth));
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen]);

  if (isInline) {
    return (
      <div ref={inlineRef}>
        <TicketsSelector
          variants={variants}
          quantities={quantities}
          seatStep={seatStep}
          maxSeats={maxSeats}
          onQuantitiesChange={onQuantitiesChange}
          errorMessage={errorMessage}
        />
      </div>
    );
  }

  return (
    <>
      <Stack gap="xsmall">
        <button
          ref={triggerRef}
          type="button"
          onClick={toggleOpen}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-invalid={tone === 'critical' ? true : undefined}
          aria-describedby={hintMax !== undefined ? hintId : undefined}
          className={cx(
            triggerStyle,
            tone === 'critical' && triggerCriticalStyle,
            disabled && triggerDisabledStyle
          )}
        >
          <Stack
            direction="row"
            align="center"
            justify="space-between"
            gap="xsmall"
            className={css({ width: 'full', paddingInline: 'xsmall' })}
          >
            <Stack direction="column" align="flex-start" gap="micro">
              <Stack direction="row" align="center" gap="xsmall">
                <TicketIcon />
                <Text style="body-large-bold">
                  <FormattedMessage
                    defaultMessage="Tickets"
                    description="Tickets field label"
                  />
                </Text>
              </Stack>
              <Text
                style="body-large-regular"
                className={css({ color: 'content.tertiary' })}
              >
                {summary}
              </Text>
            </Stack>
            <span
              aria-hidden="true"
              className={css({
                width: '[12px]',
                height: '[12px]',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'content.tertiary',
              })}
            >
              <ChevronDownIcon />
            </span>
          </Stack>
        </button>
        {hintMax !== undefined && (
          <PerBookingMaxHint id={hintId} max={hintMax} />
        )}
        {errorMessage && (
          <Text
            role="alert"
            style="body-small-regular"
            className={css({ color: 'status.negative.bold' })}
          >
            {errorMessage}
          </Text>
        )}
      </Stack>

      {/* Desktop: popover anchored to the trigger, committing live. */}
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen && !isMobile}
        onOpenChange={setIsOpen}
        placement="bottom end"
        // A modal popover makes the rest of the page inert, so the visitor's
        // next click (Continue, a time slot) only dismisses it and has to be
        // repeated. Selections commit live, so there is nothing to confirm
        // before acting elsewhere: the popover closes when focus leaves it and
        // the click goes through.
        isNonModal
        // Pressing the trigger moves focus onto it before its click fires.
        // Closing on that blur lets the click's toggle see a closed picker
        // and reopen it, so the trigger could never close the popover.
        shouldCloseOnInteractOutside={(element) =>
          !triggerRef.current?.contains(element)
        }
        className={popoverStyle}
        style={triggerWidth ? { width: triggerWidth } : undefined}
      >
        {/* A non-modal popover drops its own dialog role and focus-on-open;
            the Dialog keeps both for keyboard and screen-reader users. */}
        <Dialog
          aria-label={intl.formatMessage({
            defaultMessage: 'Select tickets',
            description: 'Accessible name for the ticket picker popover',
          })}
          className={dialogStyle}
        >
          <PickerRows
            variants={variants}
            quantities={quantities}
            seatStep={seatStep}
            maxSeats={maxSeats}
            onChange={onQuantitiesChange}
          />
        </Dialog>
      </Popover>

      {/* Mobile: bottom-sheet drawer with staged edits, committed on Save. */}
      {isOpen && isMobile && (
        <TicketsDrawer
          variants={variants}
          quantities={quantities}
          seatStep={seatStep}
          maxSeats={maxSeats}
          onSave={(next) => {
            onQuantitiesChange(next);
            setIsOpen(false);
            onSave?.(next);
          }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

const triggerStyle = css({
  display: 'block',
  width: 'full',
  padding: 'small',
  borderRadius: 'medium',
  background: 'surface.raised',
  borderWidth: '[1px]',
  borderStyle: 'solid',
  borderColor: 'border.interactive',
  boxShadow: 'medium',
  cursor: 'interactive',
  textAlign: 'left',
});

// Critical tone — negative-status border, mirroring Leaf form fields.
const triggerCriticalStyle = css({
  borderColor: 'status.negative.bold',
});

// Disabled — dimmed and non-interactive, mirroring Leaf form fields.
const triggerDisabledStyle = css({
  opacity: '[0.5]',
  cursor: '[not-allowed]',
  boxShadow: '[none]',
});

const popoverStyle = css({
  // Width is set inline to the measured trigger width; this floor keeps a
  // narrow field from crushing the picker rows.
  minWidth: '[280px]',
  background: 'surface.raised',
  borderRadius: 'medium',
  borderWidth: '[1px]',
  borderStyle: 'solid',
  borderColor: 'border.interactive',
  boxShadow: 'medium',
  padding: 'medium',
  outline: 'none',
  // react-aria caps the popover's maxHeight to the available viewport space;
  // without a scroll container the overflowing rows are unreachable. Let the
  // popover itself scroll so every variant/add-on stays bookable.
  overflowY: 'auto',
});

const dialogStyle = css({
  outline: 'none',
});
